import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { fetchSocialLinks, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { findCountry } from "@/lib/countries";
import {
  MagnifyingGlass,
  ClockCounterClockwise,
  List,
  X,
  User,
  SignOut,
  Shield,
  Globe,
  XLogo,
  LinkedinLogo,
  FacebookLogo,
  YoutubeLogo,
  InstagramLogo,
  GithubLogo,
  TelegramLogo,
  TiktokLogo,
  DiscordLogo,
  RedditLogo,
  ThreadsLogo,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { BrandMark } from "@/components/BrandMark";
import AppUpdateBanner from "@/components/AppUpdateBanner";

const PLATFORM_CONFIG = {
  twitter:   { Icon: XLogo,        hover: "hover:text-slate-700",  label: "X (Twitter)"  },
  x:         { Icon: XLogo,        hover: "hover:text-slate-700",  label: "X"             },
  linkedin:  { Icon: LinkedinLogo, hover: "hover:text-blue-600",   label: "LinkedIn"      },
  facebook:  { Icon: FacebookLogo, hover: "hover:text-blue-600",   label: "Facebook"      },
  instagram: { Icon: InstagramLogo,hover: "hover:text-pink-500",   label: "Instagram"     },
  youtube:   { Icon: YoutubeLogo,  hover: "hover:text-red-500",    label: "YouTube"       },
  github:    { Icon: GithubLogo,   hover: "hover:text-slate-900",  label: "GitHub"        },
  telegram:  { Icon: TelegramLogo, hover: "hover:text-blue-500",   label: "Telegram"      },
  tiktok:    { Icon: TiktokLogo,   hover: "hover:text-slate-900",  label: "TikTok"        },
  discord:   { Icon: DiscordLogo,  hover: "hover:text-indigo-500", label: "Discord"       },
  reddit:    { Icon: RedditLogo,   hover: "hover:text-orange-500", label: "Reddit"        },
  threads:   { Icon: ThreadsLogo,  hover: "hover:text-slate-900",  label: "Threads"       },
  whatsapp:  { Icon: WhatsappLogo, hover: "hover:text-green-500",  label: "WhatsApp"      },
};

const NAV_LINKS = [
  { to: "/",         label: "News",        end: true  },
  { to: "/trending", label: "Trending AI",  end: false },
  { to: "/ai-tools", label: "AI Tools",   end: false },
  // { to: "/pricing",  label: "Pricing",    end: false }, // HIDDEN: re-enable when ready
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPage = location.pathname === "/admin";
  const { user, isAdmin, logout, settings } = useAuth();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [socialLinks, setSocialLinks] = useState([]);
  const [logoError, setLogoError] = useState(false);
  const prevLogoUrl = useRef(settings?.logo_url);
  if (prevLogoUrl.current !== settings?.logo_url) {
    prevLogoUrl.current = settings?.logo_url;
    if (logoError) setLogoError(false);
  }
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchSocialLinks()
      .then((links) => { if (Array.isArray(links)) setSocialLinks(links); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const v = q.trim();
    if (v.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(v)}`);
    setMobileOpen(false);
  };

  const country = findCountry(user?.country);

  const isNewsRoute =
    location.pathname === "/" ||
    location.pathname.startsWith("/category/") ||
    location.pathname.startsWith("/news/") ||
    location.pathname.startsWith("/article/");

  const navLinkClass = ({ isActive }) =>
    `text-sm font-medium px-3 py-2 rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-blue-700 bg-blue-50"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
    }`;

  if (isAdminPage) {
    return (
      <div className="min-h-screen bg-slate-50 overflow-x-hidden">
        <main className="w-full">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      <AppUpdateBanner />
      <header
        className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200"
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 gap-4">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="logo-link">
              {settings?.logo_url && !logoError ? (
                <img
                  src={settings.logo_url}
                  alt="TrendMing"
                  className="h-8 w-auto max-w-[120px] object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <BrandMark size={32} className="transition-transform group-hover:scale-105" />
              )}
              <span className="font-bold text-xl text-slate-900 tracking-tight">TrendMing</span>
            </Link>

            {/* Primary nav (desktop) */}
            <nav className="hidden lg:flex items-center gap-1 flex-1 max-w-2xl mx-4" aria-label="Primary navigation">
              {NAV_LINKS.map(({ to, label, end }) => (
                <NavLink key={to} to={to} end={end} className={navLinkClass} data-testid={`nav-${label.toLowerCase().replace(/\s+/, "-")}`}>
                  {label}
                </NavLink>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Desktop search */}
              <form
                onSubmit={onSearch}
                className="hidden md:flex items-center bg-slate-50 border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30 transition-all rounded-lg max-w-xs"
                data-testid="header-search-form"
              >
                <MagnifyingGlass size={15} className="ml-3 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search news…"
                  className="bg-transparent w-full px-3 py-2 text-sm outline-none placeholder:text-slate-400 text-slate-900"
                  data-testid="header-search-input"
                />
              </form>

              {/* Auth */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenu((v) => !v)}
                    className="flex items-center gap-2 px-3 py-2 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    data-testid="user-menu-toggle"
                    aria-expanded={userMenu}
                    aria-haspopup="true"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
                        {(user.name?.[0] || user.email?.[0] || "U").toUpperCase()}
                      </div>
                    )}
                    <span className="hidden md:inline text-xs font-medium text-slate-700">
                      {user.name || user.email.split("@")[0]}
                    </span>
                    {isAdmin && (
                      <Shield size={12} className="text-blue-600" weight="fill" data-testid="admin-badge" />
                    )}
                  </button>
                  {userMenu && (
                    <div
                      className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
                      data-testid="user-menu"
                      role="menu"
                    >
                      <div className="px-4 py-3 border-b border-slate-100">
                        <div className="text-xs text-slate-500 truncate">{user.email}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <Globe size={10} /> {country.name}
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setUserMenu(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors focus:outline-none focus:bg-slate-50"
                        role="menuitem"
                        data-testid="menu-profile-link"
                      >
                        <User size={14} /> Profile
                      </Link>
                      <Link
                        to="/history"
                        onClick={() => setUserMenu(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors focus:outline-none focus:bg-slate-50"
                        role="menuitem"
                        data-testid="menu-history-link"
                      >
                        <ClockCounterClockwise size={14} /> History
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenu(false)}
                          className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors focus:outline-none focus:bg-blue-50"
                          role="menuitem"
                          data-testid="menu-admin-link"
                        >
                          <Shield size={14} weight="fill" /> Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setUserMenu(false); navigate("/"); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100 text-left focus:outline-none focus:bg-slate-50"
                        role="menuitem"
                        data-testid="menu-logout"
                      >
                        <SignOut size={14} /> Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    data-testid="header-login-link"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="hidden md:inline-flex items-center px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-700 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500/30"
                    data-testid="header-signup-link"
                  >
                    Get started
                  </Link>
                </>
              )}

              {/* Hamburger */}
              <button
                className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                aria-label="Menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
              >
                {mobileOpen ? <X size={20} /> : <List size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Category sub-header — shown on news/home routes */}
        {isNewsRoute && !isAdminPage && (
          <div className="border-t border-slate-100 bg-white/95">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <nav
                className="flex items-center gap-1 overflow-x-auto py-2 hide-scrollbar"
                aria-label="News categories"
              >
                {CATEGORIES.map(({ slug, label }) => (
                  <NavLink
                    key={slug}
                    to={slug === "top" ? "/" : `/category/${slug}`}
                    end={slug === "top"}
                    className={({ isActive }) =>
                      `text-xs font-semibold px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
                        isActive
                          ? "bg-blue-600 text-white"
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Mobile drawer */}
        {mobileOpen && (
          <div
            id="mobile-nav"
            className="lg:hidden border-t border-slate-200 bg-white shadow-lg"
            data-testid="mobile-nav"
          >
            {/* Search */}
            <form onSubmit={onSearch} className="p-4 border-b border-slate-100">
              <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30 transition-all">
                <MagnifyingGlass size={15} className="ml-3 text-slate-400 shrink-0" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search news…"
                  className="bg-transparent w-full px-3 py-2.5 text-sm outline-none text-slate-900 placeholder:text-slate-400"
                  data-testid="mobile-search-input"
                />
              </div>
            </form>

            {/* Nav links */}
            <div className="py-1">
              {NAV_LINKS.map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm font-medium min-h-[44px] ${
                      isActive ? "text-blue-700 bg-blue-50" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </div>

            {/* Auth (logged in) */}
            {user && (
              <div className="border-t border-slate-100 py-1">
                <Link to="/profile" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">
                  <User size={16} /> Profile
                </Link>
                <Link to="/history" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">
                  <ClockCounterClockwise size={16} /> History
                </Link>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-blue-600 hover:bg-blue-50 min-h-[44px]">
                    <Shield size={16} weight="fill" /> Admin Panel
                  </Link>
                )}
                <button
                  onClick={() => { logout(); setMobileOpen(false); navigate("/"); }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]"
                >
                  <SignOut size={16} /> Sign out
                </button>
              </div>
            )}

            {/* Auth (logged out) */}
            {!user && (
              <div className="grid grid-cols-2 border-t border-slate-200">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="p-4 text-sm font-medium text-center text-slate-700 border-r border-slate-200 hover:bg-slate-50 min-h-[44px] flex items-center justify-center" data-testid="mobile-login">
                  Sign in
                </Link>
                <Link to="/signup" onClick={() => setMobileOpen(false)} className="p-4 text-sm font-semibold text-center bg-slate-900 text-white hover:bg-slate-700 min-h-[44px] flex items-center justify-center" data-testid="mobile-signup">
                  Get started
                </Link>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="w-full">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 mt-0 bg-white" data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-12">

          {/* Main grid: brand + link columns */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">

            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <BrandMark size={24} />
              <p className="mt-3 text-sm text-slate-500 leading-relaxed max-w-[220px]">
                The AI Intelligence Platform. Track AI news, tools, companies, product launches, and market intelligence in one place.
              </p>
            </div>

            {/* TRENDING AI */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-slate-900 uppercase mb-4">Trending AI</h4>
              <ul className="space-y-2.5">
                {[
                  { to: "/trending",              label: "Dashboard"    },
                  { to: "/trending-ai/news",       label: "AI News"      },
                  { to: "/trending-ai/models",     label: "AI Models"    },
                  { to: "/trending-ai/companies",  label: "AI Companies" },
                  { to: "/trending-ai/startups",   label: "AI Startups"  },
                  { to: "/trending-ai/benchmarks", label: "Benchmarks"   },
                  { to: "/ai-tools",               label: "AI Tools"     },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* SEO TOOLKIT */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-slate-900 uppercase mb-4">SEO Toolkit</h4>
              <ul className="space-y-2.5">
                {[
                  { to: "/seo-studio", label: "SEO Studio"      },
                  { to: "/seo-studio", label: "Meta generator"  },
                  { to: "/seo-studio", label: "Schema builder"  },
                  { to: "/seo-studio", label: "Social captions" },
                ].map(({ to, label }) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-slate-500 hover:text-slate-900 transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* COMPANY */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-slate-900 uppercase mb-4">Company</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about"   className="text-sm text-slate-500 hover:text-slate-900 transition-colors">About us</Link></li>
                <li><Link to="/contact" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Contact</Link></li>
                {/* <li><Link to="/pricing" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Pricing</Link></li> */}{/* HIDDEN: re-enable when ready */}
                <li><Link to="/terms"   className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Terms</Link></li>
              </ul>
            </div>

            {/* ACCOUNT */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-slate-900 uppercase mb-4">Account</h4>
              <ul className="space-y-2.5">
                <li><Link to="/profile" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Profile</Link></li>
                <li><Link to="/history" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">History</Link></li>
                {/* <li><Link to="/pricing" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Upgrade plan</Link></li> */}{/* HIDDEN: re-enable when ready */}
              </ul>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-200 mt-10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} TrendMing. All rights reserved.
            </p>
            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3" aria-label="Follow TrendMing on social media">
                {socialLinks.map(({ platform, url, handle }) => {
                  const cfg = PLATFORM_CONFIG[platform?.toLowerCase()];
                  if (!cfg || !url) return null;
                  const { Icon, hover, label } = cfg;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`TrendMing on ${label}${handle ? ` (${handle})` : ""}`}
                      className={`text-slate-400 ${hover} transition-colors`}
                    >
                      <Icon size={16} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </footer>
    </div>
  );
}
